/*
 * Copyright (C) 2026 Fluxer Contributors
 *
 * This file is part of Fluxer.
 *
 * Fluxer is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * Fluxer is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with Fluxer. If not, see <https://www.gnu.org/licenses/>.
 */

import styles from '@app/components/channel/ChannelMembers.module.css';
import {MemberListContainer} from '@app/components/channel/MemberListContainer';
import {MemberListItem} from '@app/components/channel/MemberListItem';
import {MemberListUnavailableFallback} from '@app/components/channel/shared/MemberListUnavailableFallback';
import {OutlineFrame} from '@app/components/layout/OutlineFrame';
import {resolveMemberListPresence} from '@app/hooks/useMemberListPresence';
import {useMemberListSubscription} from '@app/hooks/useMemberListSubscription';
import type {ChannelRecord} from '@app/records/ChannelRecord';
import type {GuildMemberRecord} from '@app/records/GuildMemberRecord';
import type {GuildRecord} from '@app/records/GuildRecord';
import type {UserRecord} from '@app/records/UserRecord';
import AuthenticationStore from '@app/stores/AuthenticationStore';
import MemberSidebarStore from '@app/stores/MemberSidebarStore';
import UserStore from '@app/stores/UserStore';
import WindowStore from '@app/stores/WindowStore';
import {buildMemberListLayout, getTotalRowsFromLayout} from '@app/utils/MemberListLayout';
import {buildMemberListRangeWindow, isIndexInMemberListRanges} from '@app/utils/MemberListRangeUtils';
import type {GroupDMMemberGroup} from '@app/utils/MemberListUtils';
import * as MemberListUtils from '@app/utils/MemberListUtils';
import * as NicknameUtils from '@app/utils/NicknameUtils';
import {ChannelTypes} from '@fluxer/constants/src/ChannelConstants';
import {MEMBER_LIST_RANGE_MAX_SPAN} from '@fluxer/constants/src/GatewayConstants';
import {GuildOperations} from '@fluxer/constants/src/GuildConstants';
import {isOfflineStatus} from '@fluxer/constants/src/StatusConstants';
import {useLingui} from '@lingui/react/macro';
import clsx from 'clsx';
import {reaction} from 'mobx';
import {observer} from 'mobx-react-lite';
import type {UIEvent} from 'react';
import {useCallback, useEffect, useMemo, useRef, useState} from 'react';

const MEMBER_ITEM_HEIGHT = 44;
const INITIAL_MEMBER_RANGE: [number, number] = [0, MEMBER_LIST_RANGE_MAX_SPAN];
const SCROLL_BUFFER_ROWS = 24;
const SCROLL_ANCHOR_TIMEOUT_MS = 35;
const PLACEHOLDER_SKELETON_COUNT = 6;
const PLACEHOLDER_REGION_HEIGHT = 240;
const DOM_PAUSE_UNFOCUS_DELAY_MS = 3 * 60 * 1000;

function useMemberListDomPause(): boolean {
	const [paused, setPaused] = useState(false);
	const timerRef = useRef<number | null>(null);

	useEffect(() => {
		const dispose = reaction(
			() => WindowStore.focused,
			(focused) => {
				if (focused) {
					if (timerRef.current != null) {
						window.clearTimeout(timerRef.current);
						timerRef.current = null;
					}
					setPaused(false);
				} else {
					timerRef.current = window.setTimeout(() => {
						timerRef.current = null;
						setPaused(true);
					}, DOM_PAUSE_UNFOCUS_DELAY_MS);
				}
			},
			{fireImmediately: true},
		);

		return () => {
			dispose();
			if (timerRef.current != null) {
				window.clearTimeout(timerRef.current);
			}
		};
	}, []);

	return paused;
}

function rangesEqual(left: Array<[number, number]>, right: Array<[number, number]>): boolean {
	if (left.length !== right.length) {
		return false;
	}

	for (let index = 0; index < left.length; index += 1) {
		const [leftStart, leftEnd] = left[index];
		const [rightStart, rightEnd] = right[index];
		if (leftStart !== rightStart || leftEnd !== rightEnd) {
			return false;
		}
	}

	return true;
}

function getSeededRandom(seed: number): number {
	const x = Math.sin(seed) * 10000;
	return x - Math.floor(x);
}

function SkeletonMemberItem({index}: {index: number}) {
	const baseSeed = (index + 1) * 17;
	const nameWidth = 40 + getSeededRandom(baseSeed) * 40;
	const statusWidth = 30 + getSeededRandom(baseSeed + 1) * 50;

	return (
		<div className={styles.skeletonItem}>
			<div className={styles.skeletonContent}>
				<div className={styles.skeletonAvatar} />
				<div className={styles.skeletonUserInfoContainer}>
					<div className={styles.skeletonName} style={{width: `${Math.min(nameWidth, 95)}%`}} />
					<div className={styles.skeletonStatus} style={{width: `${Math.min(statusWidth, 95)}%`}} />
				</div>
			</div>
		</div>
	);
}

interface GroupDMMemberListGroupProps {
	group: GroupDMMemberGroup;
	channelId: string;
	ownerId: string | null;
}

const GroupDMMemberListGroup = observer(({group, channelId, ownerId}: GroupDMMemberListGroupProps) => (
	<div className={styles.groupContainer}>
		<div className={styles.groupHeader}>
			{group.displayName} {'\u2014'} {group.count}
		</div>
		<div className={styles.membersList}>
			{group.users.map((user) => (
				<MemberListItem
					key={user.id}
					user={user}
					channelId={channelId}
					isOwner={user.id === ownerId}
					disableBackdrop={true}
				/>
			))}
		</div>
		<div className={styles.groupSpacer} />
	</div>
));

interface LazyMemberListGroupProps {
	guild: GuildRecord;
	group: {id: string; count: number};
	groupCount: number;
	channelId: string;
	members: Array<GuildMemberRecord>;
}

const LazyMemberListGroup = observer(({guild, group, groupCount, channelId, members}: LazyMemberListGroupProps) => {
	const {t} = useLingui();
	const groupName = useMemo(() => {
		if (group.id === 'online') {
			return t`Online`;
		}
		if (group.id === 'offline') {
			return t`Offline`;
		}

		const role = guild.getRole(group.id);
		return role?.name ?? group.id;
	}, [group.id, guild, t]);

	return (
		<div className={styles.groupContainer}>
			<div className={styles.groupHeader}>
				{groupName} {'\u2014'} {groupCount}
			</div>
			<div className={styles.membersList}>
				{members.map((member) => {
					const user = member.user;
					const userId = user.id;

					return (
						<MemberListItem
							key={userId}
							user={user}
							channelId={channelId}
							guildId={guild.id}
							isOwner={guild.isOwner(userId)}
							roleColor={member.getColorString?.() ?? undefined}
							displayName={NicknameUtils.getNickname(user, guild.id)}
							disableBackdrop={true}
						/>
					);
				})}
			</div>
			<div className={styles.groupSpacer} />
		</div>
	);
});

function SkeletonPlaceholderRegion({count, seedOffset}: {count: number; seedOffset: number}) {
	return (
		<div className={styles.placeholderRegion}>
			{Array.from({length: count}).map((_, index) => (
				<SkeletonMemberItem key={index} index={index + seedOffset} />
			))}
		</div>
	);
}

interface LazyMemberListProps {
	guild: GuildRecord;
	channel: ChannelRecord;
}

const LazyMemberList = observer(function LazyMemberList({guild, channel}: LazyMemberListProps) {
	const isDomPaused = useMemberListDomPause();
	const lastRangesRef = useRef<Array<[number, number]>>([INITIAL_MEMBER_RANGE]);
	const scrollAnchorTimeoutRef = useRef<number | null>(null);
	const memberListUpdatesDisabled = (guild.disabledOperations & GuildOperations.MEMBER_LIST_UPDATES) !== 0;

	const {subscribe} = useMemberListSubscription({
		guildId: guild.id,
		channelId: channel.id,
		enabled: !memberListUpdatesDisabled,
	});

	const memberListState = isDomPaused ? undefined : MemberSidebarStore.getList(guild.id, channel.id);
	const isLoading = !memberListState || memberListState.items.size === 0;
	const memberCount = memberListState?.memberCount ?? 0;
	const groups = memberListState?.groups ?? [];
	const layouts = useMemo(() => buildMemberListLayout(groups), [groups]);
	const totalRows = useMemo(() => {
		if (layouts.length > 0) {
			return getTotalRowsFromLayout(layouts);
		}
		return memberCount;
	}, [layouts, memberCount]);
	const subscribedRanges = memberListState?.subscribedRanges ?? [];
	const effectiveRanges = subscribedRanges.length > 0 ? subscribedRanges : lastRangesRef.current;
	const firstSubscribedIndex = effectiveRanges.length > 0 ? Math.min(...effectiveRanges.map(([start]) => start)) : 0;
	const lastSubscribedIndex = effectiveRanges.length > 0 ? Math.max(...effectiveRanges.map(([, end]) => end)) : -1;
	const hasMoreBefore = memberListState != null && firstSubscribedIndex > 0;
	const hasMoreAfter = memberListState != null && totalRows > 0 && lastSubscribedIndex < totalRows - 1;
	const topPlaceholderHeight = hasMoreBefore ? firstSubscribedIndex * MEMBER_ITEM_HEIGHT : 0;
	const bottomPlaceholderHeight =
		hasMoreAfter && totalRows > 0 ? Math.max(0, (totalRows - lastSubscribedIndex - 1) * MEMBER_ITEM_HEIGHT) : 0;
	const topSpacerHeight = Math.max(0, topPlaceholderHeight - PLACEHOLDER_REGION_HEIGHT);
	const bottomSpacerHeight = Math.max(0, bottomPlaceholderHeight - PLACEHOLDER_REGION_HEIGHT);

	const commitRangeUpdate = useCallback(
		(scrollTop: number, clientHeight: number) => {
			const nextRanges = buildMemberListRangeWindow({
				scrollTop,
				clientHeight,
				rowHeight: MEMBER_ITEM_HEIGHT,
				bufferRows: SCROLL_BUFFER_ROWS,
				totalRows: totalRows > 0 ? totalRows : undefined,
			});
			if (rangesEqual(nextRanges, lastRangesRef.current)) {
				return;
			}
			lastRangesRef.current = nextRanges;
			subscribe(nextRanges);
		},
		[subscribe, totalRows],
	);

	const handleScroll = useCallback(
		(event: UIEvent<HTMLDivElement>) => {
			const target = event.currentTarget;
			if (scrollAnchorTimeoutRef.current != null) {
				window.clearTimeout(scrollAnchorTimeoutRef.current);
			}

			scrollAnchorTimeoutRef.current = window.setTimeout(() => {
				scrollAnchorTimeoutRef.current = null;
				commitRangeUpdate(target.scrollTop, target.clientHeight);
			}, SCROLL_ANCHOR_TIMEOUT_MS);
		},
		[commitRangeUpdate],
	);

	if (memberListUpdatesDisabled) {
		return (
			<MemberListContainer channelId={channel.id}>
				<MemberListUnavailableFallback />
			</MemberListContainer>
		);
	}

	if (isDomPaused) {
		return <MemberListContainer channelId={channel.id}>{null}</MemberListContainer>;
	}

	if (isLoading) {
		return (
			<MemberListContainer channelId={channel.id}>
				<div className={styles.groupContainer}>
					<div className={clsx(styles.groupHeader, styles.skeletonHeader, styles.skeleton)} />
					<div className={styles.membersList}>
						{Array.from({length: 10}).map((_, index) => (
							<SkeletonMemberItem key={index} index={index} />
						))}
					</div>
				</div>
			</MemberListContainer>
		);
	}

	const groupedItems: Map<string, Array<GuildMemberRecord>> = new Map();
	const seenMemberIds = new Set<string>();
	const groupCounts = new Map<string, number>();
	const orderedMembers: Array<GuildMemberRecord> = [];

	for (const group of groups) {
		groupedItems.set(group.id, []);
		groupCounts.set(group.id, group.count);
	}

	const sortedItems = Array.from(memberListState.items.entries())
		.filter(([memberIndex]) => isIndexInMemberListRanges(memberIndex, effectiveRanges))
		.sort(([leftIndex], [rightIndex]) => {
			return leftIndex - rightIndex;
		});
	let groupIndex = 0;
	let groupStartIndex = 0;
	let groupEndIndex = groups.length > 0 ? groups[0].count - 1 : -1;

	for (const [memberIndex, item] of sortedItems) {
		while (groupIndex < groups.length && memberIndex > groupEndIndex) {
			groupStartIndex = groupEndIndex + 1;
			groupIndex += 1;
			if (groupIndex < groups.length) {
				groupEndIndex = groupStartIndex + groups[groupIndex].count - 1;
			}
		}

		if (groupIndex >= groups.length) {
			continue;
		}

		const member = item.data;
		const userId = member.user.id;

		if (seenMemberIds.has(userId)) {
			continue;
		}

		seenMemberIds.add(userId);
		orderedMembers.push(member);
		const members = groupedItems.get(groups[groupIndex].id);
		if (members) {
			members.push(member);
		}
	}

	const hasPresenceOnlyGroups =
		groups.length > 0 &&
		groupedItems.has('online') &&
		groupedItems.has('offline') &&
		groups.every((group) => group.id === 'online' || group.id === 'offline');
	if (hasPresenceOnlyGroups) {
		const onlineMembers: Array<GuildMemberRecord> = [];
		const offlineMembers: Array<GuildMemberRecord> = [];

		for (const member of orderedMembers) {
			const status = resolveMemberListPresence({
				guildId: guild.id,
				channelId: channel.id,
				userId: member.user.id,
				enabled: true,
			});

			if (isOfflineStatus(status)) {
				offlineMembers.push(member);
			} else {
				onlineMembers.push(member);
			}
		}

		if (groupedItems.has('online')) {
			groupedItems.set('online', onlineMembers);
		}
		if (groupedItems.has('offline')) {
			groupedItems.set('offline', offlineMembers);
		}

		const isFullyLoaded = seenMemberIds.size >= memberListState.memberCount;
		if (isFullyLoaded) {
			if (groupCounts.has('online')) {
				groupCounts.set('online', onlineMembers.length);
			}
			if (groupCounts.has('offline')) {
				groupCounts.set('offline', offlineMembers.length);
			}
		}
	}

	return (
		<MemberListContainer channelId={channel.id} onScroll={handleScroll}>
			{hasMoreBefore && (
				<>
					{topSpacerHeight > 0 && <div className={styles.placeholderSpacer} style={{height: `${topSpacerHeight}px`}} />}
					<SkeletonPlaceholderRegion
						count={PLACEHOLDER_SKELETON_COUNT}
						seedOffset={Math.max(0, firstSubscribedIndex - PLACEHOLDER_SKELETON_COUNT)}
					/>
				</>
			)}
			{groups.map((group) => {
				const members = groupedItems.get(group.id) ?? [];
				const groupCount = groupCounts.get(group.id) ?? group.count;
				if (members.length === 0) {
					return null;
				}

				return (
					<LazyMemberListGroup
						key={group.id}
						guild={guild}
						group={group}
						groupCount={groupCount}
						channelId={channel.id}
						members={members}
					/>
				);
			})}
			{hasMoreAfter && (
				<>
					<SkeletonPlaceholderRegion count={PLACEHOLDER_SKELETON_COUNT} seedOffset={Math.max(0, lastSubscribedIndex)} />
					{bottomSpacerHeight > 0 && (
						<div className={styles.placeholderSpacer} style={{height: `${bottomSpacerHeight}px`}} />
					)}
				</>
			)}
		</MemberListContainer>
	);
});

interface ChannelMembersProps {
	guild?: GuildRecord | null;
	channel: ChannelRecord;
}

export const ChannelMembers = observer(function ChannelMembers({guild = null, channel}: ChannelMembersProps) {
	if (channel.type === ChannelTypes.GROUP_DM) {
		const currentUserId = AuthenticationStore.currentUserId;
		const allUserIds = currentUserId ? [currentUserId, ...channel.recipientIds] : channel.recipientIds;
		const users = allUserIds.map((id) => UserStore.getUser(id)).filter((user): user is UserRecord => user != null);
		const memberGroups = MemberListUtils.getGroupDMMemberGroups(users);

		return (
			<OutlineFrame hideTopBorder>
				<MemberListContainer channelId={channel.id}>
					{memberGroups.map((group) => (
						<GroupDMMemberListGroup key={group.id} group={group} channelId={channel.id} ownerId={channel.ownerId} />
					))}
				</MemberListContainer>
			</OutlineFrame>
		);
	}

	if (!guild) {
		return null;
	}

	const frameSides = guild ? {left: false} : undefined;

	return (
		<OutlineFrame hideTopBorder sides={frameSides}>
			<LazyMemberList guild={guild} channel={channel} />
		</OutlineFrame>
	);
});
